import { useState } from 'react';
import { CheckSmallCircleFillIcon, ChevronRightIcon, XIcon } from '@zep-us/zus-icons';
import { ParseKeys } from 'i18next';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { cn } from '@zep/utils';
import { chartFeaturesData, chartServicesData } from '../../../../consts/landingConsts';
import { Button } from '../Button';
interface ChartFeature {
  id: string;
  label: ParseKeys<['home']>;
  values: Record<string, ParseKeys<['home']> | boolean>;
}
interface ChartService {
  id: string;
  name: string;
  logo: React.ComponentType<{
    className?: string;
  }>;
  url: string;
  color: string;
}
interface LandingChartProps {
  features?: ChartFeature[];
  services?: ChartService[];
}
const SERVICE_CHECK_COLORS: Record<string, string> = {
  zep: '#6758FF',
  'zep-quiz': '#15B09C',
  'zep-school': '#0D94FF'
};
const trackServiceClick = (serviceId: string) => {
  logger.track('landing_cta_click', {
    section_name: 'chart',
    service_name: serviceId
  });
};
const MOBILE_VISIBLE_COUNT = 5;
export const LandingChart = ({
  features = chartFeaturesData as ChartFeature[],
  services = chartServicesData as ChartService[]
}: LandingChartProps) => {
  const {
    t
  } = useTranslation(['home']);
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldShowExpandUI = features.length > MOBILE_VISIBLE_COUNT && !isExpanded;
  return <section className={cn('w-full', 'py-quiz-2xl lg:py-quiz-4xl px-quiz-md tablet:px-quiz-lg lg:px-quiz-2xl', 'flex flex-col items-center justify-center')} data-sentry-component="LandingChart" data-sentry-source-file="LandingChart.tsx">
      <div className="w-full max-w-screen-xl">
        <h2 className={cn('text-quiz-heading-xl-extrabold lg:text-quiz-display-sm-extrabold text-center', 'mb-quiz-xl lg:mb-quiz-3xl pt-quiz-md tablet:pt-quiz-xl')}>
          {t('home:landing.chart.title')}
        </h2>

        <div className="relative w-full tablet:min-w-[528px]">
          <ChartHeader services={services} data-sentry-element="ChartHeader" data-sentry-source-file="LandingChart.tsx" />

          {features.map((feature, index) => {
          const shouldHideOnMobile = index >= MOBILE_VISIBLE_COUNT;
          const isVisible = !shouldHideOnMobile || isExpanded;
          return <ChartRow key={feature.id} feature={feature} services={services} isVisible={isVisible} />;
        })}

          {shouldShowExpandUI && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[72px] bg-gradient-to-b from-transparent to-white tablet:hidden" />}
        </div>

        {shouldShowExpandUI && <div className="mt-quiz-lg px-quiz-md tablet:hidden">
            <Button variant="outlined" intent="neutral" size="md" className="w-full bg-quiz-neutral" onClick={() => setIsExpanded(true)}>
              {t('home:landing.chart.expand')}
            </Button>
          </div>}
      </div>
    </section>;
};
interface ChartHeaderProps {
  services: ChartService[];
}
const ChartHeader = ({
  services
}: ChartHeaderProps) => {
  const {
    t
  } = useTranslation(['home']);
  return <div className="grid grid-cols-3 gap-x-quiz-lg tablet:grid-cols-[minmax(160px,1fr)_repeat(3,minmax(160px,200px))]" data-sentry-component="ChartHeader" data-sentry-source-file="LandingChart.tsx">
      <div className="hidden tablet:block" />
      {services.map(service => <div key={service.id} className="flex flex-col items-start gap-quiz-lg py-quiz-md tablet:px-quiz-xs">
          <service.logo className="h-[20px] w-auto tablet:h-[28px]" />
          <Link href={service.url} target="_blank" rel="noopener noreferrer" onClick={() => trackServiceClick(service.id)}>
            <Button variant="outlined" intent="neutral" size="xs" className="text-quiz-caption-lg-semibold text-quiz-gray-900 tablet:h-quiz-2xl tablet:min-w-quiz-2xl tablet:px-quiz-sm tablet:text-quiz-body-md-semibold" rightSlot={<ChevronRightIcon className="size-quiz-md tablet:size-[20px]" />}>
              {t('home:landing.shortcut.link')}
            </Button>
          </Link>
        </div>)}
    </div>;
};
interface CellValueProps {
  value: ParseKeys<['home']> | boolean;
  serviceId: string;
}
const CellValue = ({
  value,
  serviceId
}: CellValueProps) => {
  const {
    t
  } = useTranslation(['home']);
  const checkColor = SERVICE_CHECK_COLORS[serviceId];
  if (typeof value === 'boolean') {
    return value ? <CheckSmallCircleFillIcon className="size-[20px] tablet:size-quiz-lg" style={{
      color: checkColor
    }} /> : <XIcon size="md" className="[&>*]:fill-quiz-gray-300" />;
  }
  return <div className="flex flex-col items-start gap-quiz-3xs tablet:flex-row tablet:gap-quiz-xs" data-sentry-component="CellValue" data-sentry-source-file="LandingChart.tsx">
      <CheckSmallCircleFillIcon size="md" style={{
      color: checkColor
    }} data-sentry-element="CheckSmallCircleFillIcon" data-sentry-source-file="LandingChart.tsx" />
      <span className="whitespace-nowrap text-quiz-body-sm-semibold text-quiz-gray-700 tablet:text-quiz-body-lg-medium">
        {t(value)}
      </span>
    </div>;
};
interface ChartRowProps {
  feature: ChartFeature;
  services: ChartService[];
  isVisible: boolean;
}
const ChartRow = ({
  feature,
  services,
  isVisible
}: ChartRowProps) => {
  const {
    t
  } = useTranslation(['home']);
  return <div className={cn('min-h-[64px] gap-x-quiz-lg border-b border-quiz-gray-200', 'grid-cols-3 tablet:grid-cols-[minmax(160px,1fr)_repeat(3,minmax(160px,200px))]', isVisible ? 'grid' : 'max-tablet:hidden grid')} data-sentry-component="ChartRow" data-sentry-source-file="LandingChart.tsx">
      <div className="col-span-3 flex items-center gap-quiz-xs py-quiz-md pb-quiz-xs tablet:col-span-1 tablet:pb-quiz-md">
        <span className="text-quiz-body-sm-semibold text-quiz-gray-900 tablet:min-w-[160px] tablet:text-quiz-heading-sm-bold">
          {t(feature.label)}
        </span>
      </div>
      {services.map(service => <div key={service.id} className="flex h-full items-center gap-quiz-xs py-quiz-xs pb-quiz-md tablet:py-quiz-md">
          <CellValue value={feature.values[service.id]} serviceId={service.id} />
        </div>)}
    </div>;
};